/**
 * Cross-subdomain navigation helpers shared between LandingRouter and
 * DashboardRedirect.
 *
 * Why a util?
 *   The login callback (`callback-page.js`) navigates straight to
 *   `/dashboard` after Auth0 returns, which means LandingRouter (mounted
 *   only at `/`) never gets a chance to bounce a user to the subdomain
 *   that matches their userType. So DashboardRedirect needs the same hop
 *   logic. Centralising it keeps the two callers aligned.
 *
 * Why a bare hop, not `loginWithRedirect` with cross-origin redirect_uri
 * ---------------------------------------------------------------------
 * The natural-looking fix — call `loginWithRedirect({ redirect_uri:
 * 'http://clinic.localhost:4040/auth/callback' })` from platform.* — does
 * NOT work. The Auth0 SPA SDK stores the PKCE code verifier and OAuth
 * `state` parameter in localStorage/sessionStorage scoped to the
 * **originating** origin (platform.*). When Auth0 then redirects to the
 * destination origin (clinic.*) with the code, that origin's SDK has no
 * PKCE verifier or state to validate against and immediately throws
 * `Invalid state`. PKCE/state cannot cross origins by design.
 *
 * Instead we do a bare `window.location.href` hop to the destination
 * subdomain at `/dashboard`. There, `AuthenticationGuard`
 * (`withAuthenticationRequired`) sees the unauthenticated state and
 * triggers its own `loginWithRedirect` — generated, sent, and
 * validated entirely within the destination origin's SDK. Auth0
 * silently SSOs via the tenant-domain cookie (no user prompt) and
 * round-trips back to `<destination>/auth/callback` where state
 * validation succeeds.
 *
 * Caveat: in incognito / strict-third-party-cookie modes, Auth0's
 * iframe-based silent SSO is blocked. The user then sees the Auth0
 * login page once on the new subdomain. Production with a Custom
 * Domain (`login.<env>.cosigned-labs.org` etc.) makes the cookie
 * first-party and avoids this.
 *
 * Mapping:
 *   userType 'customer' -> platform.<rest>
 *   userType 'member'   -> app.<rest>
 *   userType 'clinic'   -> clinic.<rest>
 *   userType 'admin'    -> stays on whatever subdomain the user is on
 *                          (admins are allowed everywhere; LandingRouter
 *                          falls through to /dashboard for them)
 */

export const USER_TYPE_TO_SUBDOMAIN = {
  customer: 'platform',
  member: 'app',
  clinic: 'clinic',
};

/**
 * Compute the absolute origin for a target subdomain, preserving port.
 * Bare hosts (`localhost`) get the target prepended (`clinic.localhost`).
 */
export const buildSubdomainOrigin = (target) => {
  const { protocol, port, hostname } = window.location;
  const parts = hostname.split('.');
  const restParts = parts.length > 1 ? parts.slice(1) : parts;
  const host = [target, ...restParts].join('.');
  const portSuffix = port ? `:${port}` : '';
  return `${protocol}//${host}${portSuffix}`;
};

/**
 * Bare cross-subdomain hop to `<target>.<rest>:<port>/dashboard`.
 *
 * We deliberately land on `/dashboard` (not the source pathname) so that
 * `AuthenticationGuard` is guaranteed to wrap the destination route and
 * trigger the silent SSO flow on the new origin. From /dashboard the
 * `DashboardRedirect` will then route per userType.
 */
export const hopToSubdomain = (targetSubdomain) => {
  const origin = buildSubdomainOrigin(targetSubdomain);
  window.location.href = `${origin}/dashboard`;
};

/**
 * Return the target subdomain for `userType`, or null if no hop is needed.
 *
 *   getSubdomainHopTarget('clinic', 'platform') === 'clinic'
 *   getSubdomainHopTarget('clinic', 'clinic')   === null
 *   getSubdomainHopTarget('clinic', null)       === null  (bare localhost dev)
 *   getSubdomainHopTarget(null, 'platform')     === null
 */
export const getSubdomainHopTarget = (userType, currentSubdomain) => {
  // Bare hostname (no subdomain) is the dev/CI convenience case — allow
  // anything so engineers can hit any route on `localhost` directly.
  if (currentSubdomain === null) {
    return null;
  }
  const target = USER_TYPE_TO_SUBDOMAIN[userType];
  if (!target) {
    return null;
  }
  return currentSubdomain === target ? null : target;
};
